import type { ActiveFilter, FilterConfig, FilterState } from '../types';

function normalizeRowValues(
  raw: string | string[] | null | undefined,
): Set<string> {
  if (raw == null) {
    return new Set();
  }
  if (Array.isArray(raw)) {
    return new Set(raw.filter((v): v is string => typeof v === 'string' && v.length > 0));
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return new Set([raw]);
  }
  return new Set();
}

function matchesActiveFilter<T>(
  row: T,
  filter: ActiveFilter,
  getValue: (row: T, attributeId: string) => string | string[] | null | undefined,
): boolean {
  const { attributeId, operator, values } = filter;
  if (values.length === 0) {
    return true;
  }
  const rowSet = normalizeRowValues(getValue(row, attributeId));

  if (operator === 'is') {
    return values.some((v) => rowSet.has(v));
  }
  return !values.some((v) => rowSet.has(v));
}

function skipClientSideFilter(
  f: ActiveFilter,
  filterConfigs?: readonly FilterConfig[],
): boolean {
  if (!filterConfigs?.length) {
    return false;
  }
  const cfg = filterConfigs.find((c) => c.id === f.attributeId);
  return (
    cfg?.kind === 'text-search' ||
    cfg?.kind === 'date-range' ||
    cfg?.kind === 'numeric-range' ||
    cfg?.kind === 'time-range'
  );
}

/** AND across filters; within each filter, "is" uses OR across selected values. */
export function applyFilters<T>(
  rows: readonly T[],
  state: FilterState,
  getValue: (row: T, attributeId: string) => string | string[] | null | undefined,
  filterConfigs?: readonly FilterConfig[],
): T[] {
  if (!state.filters.length) {
    return rows.slice();
  }
  return rows.filter((row) =>
    state.filters.every((f) => {
      if (skipClientSideFilter(f, filterConfigs)) {
        return true;
      }
      return matchesActiveFilter(row, f, getValue);
    }),
  );
}
